import { useState } from "react";
import { describeError, isApiError } from "@/api/client";
import {
  Button,
  Dialog,
  DialogClose,
  Form,
  FormActions,
  InlineAlert,
  TextArea,
  TextField,
  useToast,
} from "@/components";
import { formatInteger } from "@/lib/format";
import { useCreateReferenceList, type ReferenceListCreated } from "./api";
import styles from "./KnowledgePage.module.css";

/** A reference list of your own from pasted names (one per line), resolved to materials by name, synonym or CAS. */
export function NewListDialog({
  open,
  onOpenChange,
  onCreated,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated: (id: string) => void;
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="New reference list"
      description="Paste one material per line: names, trade names, synonyms or CAS numbers."
      size="lg"
    >
      {open ? <NewListForm onCreated={onCreated} onDone={() => onOpenChange(false)} /> : null}
    </Dialog>
  );
}

function NewListForm({ onCreated, onDone }: { onCreated: (id: string) => void; onDone: () => void }) {
  const create = useCreateReferenceList();
  const toast = useToast();
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ReferenceListCreated | null>(null);

  function submit() {
    const found: Record<string, string> = {};
    if (!name.trim()) found.name = "Name the list";
    if (!text.trim()) found.text = "Paste at least one material name";
    setErrors(found);
    if (Object.keys(found).length) return;
    create.mutate(
      { name: name.trim(), text },
      {
        onSuccess: (created) => {
          onCreated(created.reference_list.id);
          if (!created.unresolved.length && !created.ambiguous.length) {
            toast.success(
              `${created.reference_list.name} created: all ${formatInteger(created.item_count)} names matched`,
            );
            onDone();
          } else {
            setResult(created);
          }
        },
        onError: (error) => {
          if (isApiError(error, 409)) setErrors({ name: error.detail });
        },
      },
    );
  }

  if (result) {
    return (
      <div className={styles.report}>
        <InlineAlert
          tone={result.unresolved.length ? "warning" : "success"}
          title={`${result.reference_list.name} created`}
        >
          {formatInteger(result.resolved_count)} of {formatInteger(result.item_count)} names matched a material.
        </InlineAlert>
        {result.unresolved.length ? (
          <section>
            <h3 className={styles.sectionTitle}>Not matched ({formatInteger(result.unresolved.length)})</h3>
            <p className={styles.muted}>
              These stay on the list as unknown names and are left out of the coverage share.
            </p>
            <ol className={styles.reportList}>
              {result.unresolved.map((row) => (
                <li key={row.position} value={row.position}>
                  {row.raw_name}
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        {result.ambiguous.length ? (
          <section>
            <h3 className={styles.sectionTitle}>
              Matched more than one material ({formatInteger(result.ambiguous.length)})
            </h3>
            <p className={styles.muted}>The first was used; the others share its name or substance.</p>
            <ol className={styles.reportList}>
              {result.ambiguous.map((row) => (
                <li key={row.position} value={row.position}>
                  {row.raw_name}: {row.chosen.name} (also {row.others.map((other) => other.name).join(", ")})
                </li>
              ))}
            </ol>
          </section>
        ) : null}
        <FormActions>
          <Button variant="primary" onClick={onDone}>
            Done
          </Button>
        </FormActions>
      </div>
    );
  }

  return (
    <Form onSubmit={submit} errors={errors}>
      <div className={styles.fields}>
        <TextField label="List name" name="name" value={name} onValueChange={setName} required error={errors.name} />
        <TextArea
          label="Materials"
          name="text"
          mono
          rows={12}
          value={text}
          onValueChange={setText}
          placeholder={"Hedione\nIso E Super\n78-70-6\nBergamot oil FCF"}
          description="Bullets and numbering are ignored. ⌘/Ctrl+Enter creates the list."
          error={errors.text}
        />
        {create.error && !Object.keys(errors).length ? (
          <InlineAlert tone="danger" title="Couldn’t create the list">
            {describeError(create.error)}
          </InlineAlert>
        ) : null}
      </div>
      <FormActions>
        <DialogClose />
        <Button variant="primary" type="submit" pending={create.isPending} pendingLabel="Matching names…">
          Create list
        </Button>
      </FormActions>
    </Form>
  );
}
